const ORIGINS = {
  local: {
    pages: "https://pages.hairbook.lvh.me",
    salonjobs: "https://salonjobs.hairbook.lvh.me",
    salonpage: "https://salonpage.hairbook.lvh.me",
    world: "https://world.hairbook.lvh.me",
    hairbook: "https://hairbook.jp",
  },
  dev: {
    pages: "https://pages.hairbook.dev",
    salonjobs: "https://salonjobs.hairbook.dev",
    salonpage: "https://salonpage.hairbook.dev",
    world: "https://world.hairbook.dev",
    hairbook: "https://hairbook.jp",
  },
  main: {
    pages: "https://hairbook-pages.fly.dev",
    salonjobs: "https://salonjobs.hairbook.jp",
    salonpage: "https://salonpage.hairbook.jp",
    world: "https://world.hairbook.jp",
    hairbook: "https://hairbook.jp",
  },
} as const;

const ACCOUNT_ORIGINS = {
  local: "https://hairbook.lvh.me",
  dev: "https://hairbook.dev",
  main: "https://hairbook.jp",
} as const;

export type AppOriginId = keyof (typeof ORIGINS)["main"];
type AppEnvironment = keyof typeof ORIGINS;

function currentEnvironment(): AppEnvironment {
  if (typeof window === "undefined") return "main";

  const { host } = window.location;
  if (host === "hairbook.lvh.me" || host.endsWith(".hairbook.lvh.me")) {
    return "local";
  }
  if (host === "hairbook.dev" || host.endsWith(".hairbook.dev")) {
    return "dev";
  }
  return "main";
}

export function isProduction(): boolean {
  return currentEnvironment() === "main";
}

export function appOrigin(id: AppOriginId): string {
  return ORIGINS[currentEnvironment()][id];
}

export function hairbookAccountOrigin(): string {
  return ACCOUNT_ORIGINS[currentEnvironment()];
}

export function hostnameOf(host: string): string {
  return host.split(":")[0]?.toLowerCase() ?? "";
}

export function isSalonpageHost(host: string): boolean {
  const hostname = hostnameOf(host);
  return (
    hostname === "salonpage.hairbook.jp" ||
    hostname === "salonpage.hairbook.dev" ||
    hostname === "salonpage.hairbook.lvh.me"
  );
}

/** @deprecated Use isSalonpageHost. */
export function isSalonpageGatewayHost(host: string): boolean {
  return isSalonpageHost(host);
}

export function isMagazinePublicHost(host: string): boolean {
  const hostname = hostnameOf(host);
  return (
    hostname === "magazine.hairbook.jp" ||
    hostname === "magazine.hairbook.dev" ||
    hostname === "magazine.hairbook.lvh.me"
  );
}

function isAccountsHost(host: string): boolean {
  const hostname = hostnameOf(host);
  return hostname === "accounts.hairbook.jp" || hostname === "accounts.hairbook.dev";
}

function originHost(origin: string): string {
  try {
    return new URL(origin).host;
  } catch {
    return "";
  }
}

/**
 * OTP / session JSON (`/api/verifies`, `/api/session`) の origin。
 *
 * salonpage と magazine の公開ホストは hairbook-gateway（Worker 名
 * `salonpage-gateway` / `salonpage-gateway-staging`）が terminate する前提で
 * same-origin (`""`) にする。`accounts.hairbook.jp` へは戻さない。
 * `VITE_ACCOUNTS_ORIGIN` が accounts ホストなら salonpage と同様無視する。
 * magazine 側は GW が `magazine.hairbook.jp` / `.dev` を受け持つ cutover に依存する。
 *
 * origin を same-origin にしても、magazine の公開記事（`/`・`/articles/:id`）を
 * ログインウォールで囲んではいけない。session 判定の呼び出し元は `/redirect` だけ。
 */
export function resolveAuthApiOrigin(input: {
  host?: string;
  override?: string | null;
}): string {
  const override = String(input.override || "").replace(/\/$/, "");
  if (override && !isAccountsHost(originHost(override))) return override;
  if (input.host && (isSalonpageHost(input.host) || isMagazinePublicHost(input.host))) {
    return "";
  }
  return "";
}

function viteAuthOriginOverride(): string | undefined {
  const env = import.meta.env as { VITE_ACCOUNTS_ORIGIN?: string; VITE_GATEWAY_ORIGIN?: string };
  const value = env.VITE_ACCOUNTS_ORIGIN || env.VITE_GATEWAY_ORIGIN;
  return value ? String(value) : undefined;
}

export function authApiOrigin(): string {
  return resolveAuthApiOrigin({
    override: viteAuthOriginOverride(),
    host: typeof window === "undefined" ? undefined : window.location.host,
  });
}

/** @deprecated Use authApiOrigin(). Same-origin on salonpage / magazine GW hosts. */
export function accountsWorkerOrigin(): string {
  return authApiOrigin();
}

export function qs(params?: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params ?? {}).filter(([, v]) => v !== undefined && v !== "");
  if (!entries.length) return "";
  return "?" + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join("&");
}
